import { UploadVideoDocument } from "@/generated/graphql";
import { useAppSelector } from "@/store/hooks";
import { MediaUsageContextSchema } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";
import { useState } from "react";
import toast from "react-hot-toast";
import type { MediaInput } from "./handlers/types";

/**
 * Handles uploading a local video file and calling the provided handler
 * with the result — keeping local file drops consistent with dnd-from-media drops.
 *
 * Usage:
 *   const { handleFileDrop, isUploading } = useLocalVideoUpload(
 *     (media) => handlers.addVideo(media)
 *   );
 */
export function useLocalVideoUpload(onUploaded: (media: MediaInput) => void) {
  const [isUploading, setIsUploading] = useState(false);
  // Tags the Media House copy by the slide being edited. Null outside the slide editor
  // (the lesson generator's draft editor), where the server falls back.
  const slideTemplate = useAppSelector((state) => state.slideEditor.present.slide?.template);
  const [uploadVideoMutation] = useMutation(UploadVideoDocument);

  const handleFileDrop = async (file: File) => {
    if (!file.type.startsWith("video/")) {
      toast.error("Only video files are supported");
      return;
    }

    setIsUploading(true);
    toast.loading("Uploading video...", { id: "videoUploadToast" });

    try {
      const upload = await uploadVideoMutation({
        variables: {
          file,
          folder: "media/video",
          usageContext: MediaUsageContextSchema.Values.slideEditor,
          slideTemplate,
        },
      });

      if (!upload.data || upload.error) throw new Error();

      const { id, url, filename } = upload.data.uploadVideo;
      onUploaded({ id, src: url, filename });
      toast.success("Video uploaded", { id: "videoUploadToast" });
    } catch (_error) {
      toast.error("Failed to upload video", { id: "videoUploadToast" });
    } finally {
      setIsUploading(false);
    }
  };

  return { handleFileDrop, isUploading };
}
